import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  CreditCard,
  Dumbbell,
  TrendingUp,
  LogOut,
  Settings,
  MessageSquare,
  Quote,
  Bell,
  BarChart3,
  Flame,
  ChevronRight,
  Calendar,
  AlertTriangle,
  Send,
  Mail,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingToUser, setSendingToUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    delete axios.defaults.headers.common["Authorization"];
    navigate("/admin");
    toast.success("Logged out");
  };

  const sendBulkReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-bulk-payment-reminders`);
      toast.success(`Sent ${response.data.reminders_sent} payment reminders!`);
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const sendSingleReminder = async (userId) => {
    setSendingToUser(userId);
    try {
      await axios.post(`${API}/admin/send-payment-reminder/${userId}`);
      toast.success("Reminder sent!");
    } catch (error) {
      toast.error("Failed to send reminder");
    } finally {
      setSendingToUser(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">ADMIN DASHBOARD</span>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/admin/dashboard" className="text-primary font-semibold uppercase text-sm tracking-wider">Overview</Link>
              <Link to="/admin/users" className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Users</Link>
              <Link to="/admin/content" className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Content</Link>
              <Link to="/admin/payments" className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Payments</Link>
            </nav>
            
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Total Users</span>
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <div className="text-4xl font-black text-primary font-mono">{stats?.stats?.total_users || 0}</div>
                <div className="text-sm text-accent">+{stats?.stats?.monthly_signups || 0} this month</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Subscribers</span>
                  <CreditCard className="h-4 w-4 text-accent" />
                </div>
                <div className="text-4xl font-black text-accent font-mono">{stats?.stats?.active_subscriptions || 0}</div>
                <div className="text-sm text-muted-foreground">Active plans</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Workouts</span>
                  <Dumbbell className="h-4 w-4 text-orange-500" />
                </div>
                <div className="text-4xl font-black text-orange-500 font-mono">{stats?.stats?.total_workouts || 0}</div>
                <div className="text-sm text-muted-foreground">Completed total</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className={`border-border ${(stats?.stats?.unpaid_users || 0) > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-card'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Unpaid</span>
                  <AlertTriangle className={`h-4 w-4 ${(stats?.stats?.unpaid_users || 0) > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                </div>
                <div className={`text-4xl font-black font-mono ${(stats?.stats?.unpaid_users || 0) > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {stats?.stats?.unpaid_users || 0}
                </div>
                <div className="text-sm text-muted-foreground">Need payment</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Unpaid Users Alert */}
        {(stats?.stats?.unpaid_users || 0) > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="uppercase tracking-tighter flex items-center gap-2 text-red-500">
                    <AlertTriangle className="h-5 w-5" />
                    Clients Need Payment ({stats?.stats?.unpaid_users})
                  </CardTitle>
                  <Button 
                    onClick={sendBulkReminders}
                    disabled={sendingReminders}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {sendingReminders ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send All Reminders
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(stats?.unpaid_users || []).map((user, i) => (
                    <div key={user.id || i} className="flex items-center justify-between p-2 bg-background/50 rounded-sm">
                      <div>
                        <div className="font-medium text-sm">{user.name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => sendSingleReminder(user.id)}
                          disabled={sendingToUser === user.id}
                          className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                        >
                          {sendingToUser === user.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Mail className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Subscription Breakdown */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Subscription Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats?.subscription_breakdown || {}).map(([tier, count]) => (
                  <div key={tier} className="flex items-center justify-between">
                    <span className="uppercase font-bold text-sm">{tier}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(count / (stats?.stats?.active_subscriptions || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="font-mono font-bold">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-card border-border lg:col-span-2">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/admin/users">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <Users className="h-8 w-8 text-primary mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Manage Users</div>
                    <div className="text-sm text-muted-foreground">View, edit, manage subscriptions</div>
                  </div>
                </Link>
                
                <Link to="/admin/content">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <Dumbbell className="h-8 w-8 text-accent mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Content Manager</div>
                    <div className="text-sm text-muted-foreground">Exercises, quotes, workouts</div>
                  </div>
                </Link>
                
                <Link to="/admin/announcements">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <Bell className="h-8 w-8 text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Announcements</div>
                    <div className="text-sm text-muted-foreground">Create app announcements</div>
                  </div>
                </Link>
                
                <Link to="/admin/payments">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <CreditCard className="h-8 w-8 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Payments</div>
                    <div className="text-sm text-muted-foreground">View transactions, revenue</div>
                  </div>
                </Link>
                
                <Link to="/admin/subscriptions">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <TrendingUp className="h-8 w-8 text-green-500 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Subscriptions</div>
                    <div className="text-sm text-muted-foreground">Manage plans, revenue stats</div>
                  </div>
                </Link>
                
                <Link to="/admin/profile-views">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <BarChart3 className="h-8 w-8 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Profile Views</div>
                    <div className="text-sm text-muted-foreground">Track user engagement</div>
                  </div>
                </Link>
                
                <Link to="/admin/affiliates">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <Users className="h-8 w-8 text-green-500 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Affiliates</div>
                    <div className="text-sm text-muted-foreground">Manage affiliates & payouts</div>
                  </div>
                </Link>
                
                <Link to="/admin/emails">
                  <div className="p-4 bg-secondary rounded-sm hover:bg-secondary/80 transition-colors cursor-pointer group">
                    <Bell className="h-8 w-8 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold uppercase tracking-tight">Email List</div>
                    <div className="text-sm text-muted-foreground">View & export emails</div>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity & Top Performers */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-primary" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_activity?.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {stats.recent_activity.map((activity, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                      <div className="flex items-center gap-3">
                        {activity.type === 'signup' && (
                          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Users className="h-4 w-4 text-green-500" />
                          </div>
                        )}
                        {activity.type === 'workout' && (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <Dumbbell className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        {activity.type === 'checkin' && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activity.showed_up ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            {activity.showed_up ? (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            ) : (
                              <Calendar className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        )}
                        <div>
                          {activity.type === 'signup' && (
                            <>
                              <div className="font-bold text-sm">{activity.user_name} signed up</div>
                              <div className="text-xs text-muted-foreground">
                                {activity.email} • {activity.subscription_tier}
                              </div>
                            </>
                          )}
                          {activity.type === 'workout' && (
                            <>
                              <div className="font-bold text-sm">{activity.user_name}</div>
                              <div className="text-xs text-muted-foreground">
                                Completed Day {activity.day} - {activity.workout_title}
                              </div>
                            </>
                          )}
                          {activity.type === 'checkin' && (
                            <>
                              <div className="font-bold text-sm">{activity.user_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {activity.showed_up ? '✅ Showed up' : `❌ Didn't show up${activity.reason ? `: ${activity.reason}` : ''}`}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(activity.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No recent activity</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                <Flame className="h-5 w-5 text-orange-500" />
                Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.top_performers?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_performers.slice(0, 5).map((user, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          i === 0 ? 'bg-yellow-500 text-black' : 
                          i === 1 ? 'bg-gray-400 text-black' : 
                          i === 2 ? 'bg-amber-600 text-black' : 'bg-secondary text-foreground'
                        }`}>
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-bold text-sm">{user.name}</div>
                          <div className="text-xs text-muted-foreground">Day {user.current_day}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-orange-500">
                        <Flame className="h-4 w-4" />
                        <span className="font-mono font-bold">{user.streak}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No users yet</div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
